import commonActions from '@/store/data/common/actions'
import { webRequestClient } from '@/services'
import { get, toNumber, isEmpty, isNil } from 'lodash'
import helpers from './helpers'
import { errorHandler } from '@/utils/helpers'

const actions = {
    retrieveItems({ commit, dispatch, getters, rootState }) {
        commit('setIsLoading', true)
        const _route = helpers.buildRoutes(toNumber(rootState.account.accountId))
        return webRequestClient
            .get(_route.route, _route._params)
            .then(async transfers => {
                if (get(transfers, 'status') === 'KO') {
                    errorHandler('Error while retrieving transfers', commit)
                } else if (!isEmpty(transfers.list)) {
                    await dispatch('data/dsp/retrieveItems', {}, { root: true })
                    commit('setTransferList', helpers.parseData(transfers.list, getters, true))
                    commit('setTransfers', {
                        items: helpers.parseData(transfers.list, getters, false)
                    })
                }
                return transfers
            })
            .catch(error => {
                commit('setTransfersActiveError', true)
                errorHandler(error, commit)
            }).finally(() => {
                commit('setIsLoading', false)
            })
    },

    createTransfer({ commit, getters, rootState }, params) {
        const _params = {
            account_id: toNumber(rootState.account.accountId),
            data_transfers: params.data_transfers
        }
        return webRequestClient
            .post('/audience_admin/data_transfers/bulk_upload/json', _params)
            .then(response => {
                if (get(response, 'status', '') !== 'KO' && !isEmpty(response)) {
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Transfers successfully created'
                        },
                        { root: true }
                    )
                    //transfer store
                    commit('setTransfersWithSameAudienceId', {
                        items: helpers.parseData(response.list, getters, false),
                        audienceId: params.audienceId
                    })
                    commit('setTransferList', helpers.parseData([...response.list, ...rootState.data.transfer.transferList], getters, true))
                    commit('ui/transfers/setShowFormTransfer', { show: false }, { root: true })
                    commit('ui/transfers/setTransferEndDate', { endDate: null }, { root: true })
                    commit('setResetBulkSelectedRows', true)
                    //to close modal in Transfers vue
                    return { status: 'OK' }
                } else {
                    errorHandler('Unable to create transfer', commit)
                }
            })
            .catch(err => {
                const messages = err?.apiError?.message?.list.map((message) => {
                    const list = []
                    const resultErrors = message.result_errors ? Object.keys(message?.result_errors) : []
                    if(resultErrors.length > 0) {
                        resultErrors.forEach(key => {
                            const msgKey = key === 'DataTransfer' ? 'A data transfer' : key === 'label' ? 'The ' + key  : key
                            list.push(msgKey + ' ' + message?.result_errors[key] + '. ')
                        })
                    } else {
                        list.push(resultErrors)
                    }
                    return list
                })
                errorHandler(`Transfer(s) failed to create: ${messages.toString().split(',').join('')}`, commit)
                commit('ui/transfers/setErrorsForTransferForm', err?.apiError?.message?.list, { root: true })
                return { status: 'KO' }
            })
    },

    editTransfer({ commit, dispatch }, params) {
        const path = `/audience_admin/data_transfers/${params.id}.json`

        return new Promise((resolve, reject) => {
            return webRequestClient
                .put(path, params.params)
                .then(response => {
                    if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                        errorHandler(`error while updating transfer ${params.id}`, commit)
                    } else if (!isEmpty(response)) {
                        commit(
                            'notifications/setAddedNotification',
                            {
                                type: 'success',
                                message: params.notificationMessage
                            },
                            { root: true }
                        )
                        dispatch('retrieveUpdatedTransferFromAudience', { ...params, audienceId: params.audienceId})
                        resolve()
                    }
                })
                .catch(error => {
                    errorHandler(error, commit)
                    reject(error)
                })
        })
    },

    retrieveUpdatedTransferFromAudience({ commit, getters, rootState, state }, params) {
        const _route = helpers.buildRoutes(toNumber(rootState.account.accountId), params.audienceId)
        return webRequestClient
            .get(_route.route, _route._params)
            .then(transfers => {
                if (get(transfers, 'status') === 'KO') {
                    errorHandler('Error while retrieving audience transfers', commit)
                } else if (!isEmpty(transfers.list)) {
                    const keys = Object.keys(params.params)
                    const  _transfers = helpers.parseData(keys.includes('end_date') ? transfers.list.map(transfer => ({...transfer, status: 'active'})) : transfers.list, getters, true)
                    const item = _transfers.find(({id}) =>  +id === +params.id)
                    commit('setEditedTransfer', {
                        audienceId: params.audienceId,
                        items: _transfers
                    })
                    commit(
                        'data/audience/seEditedAudienceTransfers',
                        {
                            audienceId: params.audienceId,
                            transfers: _transfers
                        },
                        { root: true }
                    )

                    if(!isNil(item)) {
                        commit('setEditedFlatTransfer', {
                            transferId: params.id,
                            item: { ...item, status: keys.includes('end_date') ? 'active' : item.status }
                        })
                    }

                    if(!isNil(item) && !isNil(state.transferInfos)) {
                        if (keys.length > 1) {
                            keys.forEach(key => {
                                commit('setTransferInfosUpdated', { key, item })
                            })
                        } else {
                            commit('setTransferInfosUpdated', { key: keys[0], item })
                        }
                    }
                }
                return transfers
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },
    async bulkDeleteTransfers({ commit, rootState, getters, state, dispatch }, transferIds) {
        commit('setIsUpdatedOrDeletedLoading', true)
        const payload = {
            account_id: +rootState.account.accountId,
            ids: transferIds.map(arr => arr.id)
        }

        const request = webRequestClient.post('/audience_admin/data_transfers/bulk_delete/json', payload)
        const aborted = await dispatch('abortBulkTransfers', request, 20000)

        if (aborted?.status === 'ABORTED') {
            return
        }
        return request
            .then(deletedTransfer => {
                if (get(deletedTransfer, 'status', '') === 'KO' || isEmpty(deletedTransfer)) {
                    errorHandler(`error while deleting transfer ${transferIds}`, commit)
                } else {
                    const data = transferIds.map(transfer => ({ transferId: transfer.id, audienceId: transfer.audience_id }))
                    commit('removeTransfersFromAudiences', data)
                    commit('removeTransfersFromTransferList', data)
                    commit('ui/transfers/setSelectedTransferRowId', null, { root: true })
                    commit('setResetBulkSelectedRows', true)

                    data.forEach(({ audienceId }) => {
                        if (rootState.ui.monitoring.expandedRows.includes(toNumber(audienceId)) && isEmpty(rootState.data.transfer.list[audienceId])) {
                            commit('ui/monitoring/setExpandedRows', { id: toNumber(audienceId) }, { root: true })
                        }
                    })
                    commit('setTransfers', {
                        items: helpers.parseData(state.transferList, getters, false)
                    })
                    commit('notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Your transfers has been successfully deleted'
                        },
                        { root: true }
                    )
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('ui/monitoring/setDisplayModal', { item: 'transfer', display: false }, { root: true })
                commit('ui/transfers/setDisplayModal', { item: 'transfer', display: false }, { root: true })
                // Collapse the row if it was expanded and there no longer is active transfers for it
                commit('setIsUpdatedOrDeletedLoading', false)
                commit('ui/monitoring/setDeletedTransfer', { transferId: null, audienceId: null }, { root: true })
                commit('ui/transfers/setDeletedTransfer', { transferId: null, audienceId: null }, { root: true })
            })
    },
    deleteTransfer({ commit, state, rootState, dispatch }, { transferId, audienceId }) {
        return webRequestClient
            .delete(`/audience_admin/data_transfers/${transferId}.json`)
            .then(deletedTransfer => {
                if (get(deletedTransfer, 'status', '') === 'KO' || isEmpty(deletedTransfer)) {
                    errorHandler(`error while deleting transfer ${transferId}`, commit)
                } else {
                    if (state.list[audienceId].length === 1) {
                        commit('removeTransfersFromAudienceId', { transferId, audienceId })
                    } else {
                        commit('removeTransferFromAudienceId', { transferId, audienceId })
                    }
                    commit('removeTransferFromTransferList', transferId)
                    commit('ui/transfers/setSelectedTransferRowId', null, { root: true })
                    commit('setResetBulkSelectedRows', true)
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Your transfer has been successfully deleted'
                        },
                        { root: true }
                    )
                    dispatch('dashboardAccount/retrieveExpiringTransfers', null, { root: true})

                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('ui/monitoring/setDisplayModal', { item: 'transfer', display: false }, { root: true })
                commit('ui/transfers/setDisplayModal', { item: 'transfer', display: false }, { root: true })
                // Collapse the row if it was expanded and there no longer is active transfers for it
                if (rootState.ui.monitoring.expandedRows.includes(toNumber(audienceId)) && !rootState.data.transfer.list[audienceId]) {
                    commit('ui/monitoring/setExpandedRows', { id: toNumber(audienceId) }, { root: true })
                }
                commit('ui/monitoring/setDeletedTransfer', { transferId: null, audienceId: null }, { root: true })
                commit('ui/transfers/setDeletedTransfer', { transferId: null, audienceId: null }, { root: true })
            })
    },
    retrieveAccountsDspActiveTransfers({ commit }, params) {
        return webRequestClient
            .get('/audience_admin/data_transfers.json', {
                params: { conditions: { accounts_dsp_id: params.accounts_dsp_id, status: ['active'] }, list_limit: 0 }
            })
            .then(activeTransfers => {
                if (get(activeTransfers, 'status', '') === 'KO' || isEmpty(activeTransfers)) {
                    commit('ui/transfers/setActiveTransfers', { activeTransfers: 'unknown number of' }, { root: true })
                    errorHandler('Error while retrieve active transfers linked to this configuration', commit)
                } else {
                    commit('ui/transfers/setActiveTransfers', { activeTransfers: activeTransfers.total_result }, { root: true })
                }
            })
            .catch(error => {
                commit('ui/transfers/setActiveTransfers', { activeTransfers: 'unknown number of' }, { root: true })
                errorHandler(error, commit)
            })
    },
    deleteAccountsDspActiveTransfers({ commit, rootState }, params) {
        return webRequestClient
            .get('/audience_admin/data_transfers.json', {
                params: { conditions: { accounts_dsp_id: params.accounts_dsp_id, status: ['active'] }, list_limit: 0 }
            })
            .then(activeTransfers => {
                if (get(activeTransfers, 'status', '') === 'KO' || isEmpty(activeTransfers)) {
                    errorHandler('Error while retrieve active transfers linked to this configuration', commit)
                } else {
                    for (const transfer of activeTransfers.list) {
                        const audienceId = transfer.audience_id
                        commit('removeTransferFromAudienceId', {
                            transferId: toNumber(transfer.id),
                            audienceId: toNumber(audienceId)
                        })

                        // For each deleted transfer collapse the row if it was expanded and there no longer is active transfers for it
                        if (rootState.ui.monitoring.expandedRows.includes(toNumber(audienceId)) && isEmpty(rootState.data.transfer.list[audienceId])) {
                            commit('ui/monitoring/setExpandedRows', { id: toNumber(audienceId) }, { root: true })
                        }
                    }
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },
    resetEditBulkForm({ commit }, reset) {
        commit('setResetBulkSelectedRows', reset)
    },
    async bulkEditTransfers({ commit, rootState, getters, state, dispatch }, { list, end_date, type }) {
        commit('setIsUpdatedOrDeletedLoading', true)
        const path = `/audience_admin/data_transfers/${type}/bulk_update/json`
        const requestParams = {
            account_id: toNumber(rootState.account.accountId),
            ids: list.map(({ id }) => +id),
        }
        if (type === 'end_date') {
            requestParams.end_date = end_date
        }
        const request = webRequestClient.post(path, requestParams)
        const aborted = await dispatch('abortBulkTransfers', request, 20000)

        if (aborted?.status === 'ABORTED') {
            return
        }
        return request
            .then(() => {
                commit(
                    'notifications/setAddedNotification',
                    {
                        type: 'success',
                        message: 'Transfers successfully updated'
                    },
                    { root: true }
                )
                const responseParams = type === 'end_date' ? { end_date, endDate: end_date } : { bootstrap_status: 'pending' }
                const transferUpdated = list.map(transfer => ({ ...transfer, ...responseParams }))
                const transfers = helpers.parseData(transferUpdated, getters, true)
                transfers.forEach(transfer => {
                    commit('setEditedFlatTransfer', { transferId: transfer.id, item: {...transfer, ...responseParams } })
                })
                commit('setTransfers', {
                    items: helpers.parseData(state.transferList, getters, false)
                })
                commit('setResetBulkSelectedRows', true)

                commit('ui/transfers/setSelectedTransferRowId', null, { root: true })
                commit('ui/monitoring/setSelectedRow', { rowId: null }, { root: true })
                // commit('ui/transfers/setDisplayModal', { item: 'bulkEdit', display: false }, { root: true } )
                return { status: 'OK'}
            })
            .catch(error => {
                if(!isNil(error.apiError.message.errors)) {
                    const idsErrors = Object.keys(error.apiError.message.errors)
                    const updatedTransfers = list.filter(({id}) => !idsErrors.includes(id))
                    if(updatedTransfers.length) {
                        const transfers = helpers.parseData(updatedTransfers, getters, true)
                        transfers.forEach(transfer => {
                            commit('setEditedFlatTransfer', { transferId: transfer.id, item: {...transfer, bootstrap_status: 'pending'  } })
                        })
                        commit('setTransfers', {
                            items: helpers.parseData(state.transferList, getters, false)
                        })
                        commit('ui/transfers/setSelectedTransferRowId', null, { root: true })
                        commit('ui/monitoring/setSelectedRow', { rowId: null }, { root: true })
                        commit('setListTransferNoVolume', error.apiError.message.errors)
                        commit('setUpdatedBulkList', updatedTransfers.map(({ id }) => id ))
                    } else {
                        commit('setListTransferNoVolume', error.apiError.message.errors)
                    }
                } else {
                    errorHandler(error, commit)
                }
            }).finally(() => {
                commit('setIsUpdatedOrDeletedLoading', false)
            })
    },
    async abortBulkTransfers({commit}, request, timeout = 20000) {
        const timeoutPromise = new Promise(resolve => {
            setTimeout(() => resolve({ timeout: true }), timeout)
        })
        const first = await Promise.race([request, timeoutPromise]).catch(() => {})
        if (first?.timeout) {
            commit('ui/transfers/setDisplayModal', { item: 'bulkEdit', display: false }, { root: true })
            commit(
                'notifications/setAddedNotification',
                { type: 'success', message: 'Your operation has been launched. Please refresh the page in a few minutes.' },
                { root: true }
            )
            commit('setIsUpdatedOrDeletedLoading', false)
            commit('setResetBulkSelectedRows', true)
            commit('ui/transfers/setSelectedTransferRowId', null, { root: true })
            commit('ui/monitoring/setSelectedRow', { rowId: null }, { root: true })
            request.catch(() => {})
            return { status: 'ABORTED' }
        }
        return false
    }
}

export default { ...commonActions, ...actions }
